"use client";

import { createContext, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../services/api"; // Use the configured API instance

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem("token");
      const userData = localStorage.getItem("userData");

      if (token && userData) {
        try {
          const user = JSON.parse(userData);
          const currentTime = Date.now() / 1000;
          const tokenData = JSON.parse(atob(token.split(".")[1]));

          if (tokenData.exp && tokenData.exp < currentTime) {
            console.warn("Token expired, logging out...");
            localStorage.removeItem("token");
            localStorage.removeItem("userData");
            setCurrentUser(null);
          } else {
            setCurrentUser(user);
          }
        } catch (error) {
          console.error(
            "Error parsing token/user data, forcing logout:",
            error
          );
          localStorage.removeItem("token");
          localStorage.removeItem("userData");
          setCurrentUser(null);
        }
      }

      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (credentials) => {
    setLoading(true);
    try {
      const response = await api.post("/auth/login", credentials);
      const { token, user } = response.data;

      if (!user) {
        console.warn("User data is missing in the response");
      }

      // Store token and user data
      localStorage.setItem("token", token);
      localStorage.setItem("userData", JSON.stringify(user || {}));

      setCurrentUser(user || null); // Set user or null if undefined

      toast.success("Login successful!");

      //Wait until currentUser is set before navigating
      setTimeout(() => {
        navigate("/");
      }, 100); // Small delay to ensure the state updates before navigation

      return true;
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage =
        error.response?.data?.message || "Login failed. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userData");
    setCurrentUser(null);
    toast.success("Logout successful!");
    navigate("/login");
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
